import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useContext, createContext } from 'react'
import { api } from '@/services/api'
import { toast } from '@/lib/toast'
import type { 
  CurrentUser, 
  LoginBody, 
  LoginResponse, 
  RegisterBody, 
  RegisterResponse,
  ResetPasswordBody,
  UserChangePasswordBody,
  UpdateProfileBody
} from '@/types'

// Create context here (in .ts file, not .tsx) to avoid Fast Refresh issues
export const AuthContext = createContext<any>(undefined)

// Query keys
export const authQueryKeys = {
  all: ['auth'] as const,
  user: () => [...authQueryKeys.all, 'user'] as const,
} as const

// Current user query
export const useCurrentUser = (options = {}) => {
  return useQuery({
    queryKey: authQueryKeys.user(),
    queryFn: async () => {
      const response = await api.get<CurrentUser>('/user/me')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Login mutation
export const useLogin = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (credentials: LoginBody) => {
      const response = await api.post<LoginResponse>('/auth/login', credentials)
      return response.data
    },
    onSuccess: (data) => {
      // Store tokens
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('refresh_token', data.refresh_token)
      
      // Update user cache
      queryClient.setQueryData(authQueryKeys.user(), data.user)
      
      toast.success('auth.loginSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Login failed')
    },
  })
}

// Register mutation
export const useRegister = () => {
  return useMutation({
    mutationFn: async (userData: RegisterBody) => {
      const response = await api.post<RegisterResponse>('/auth/register', userData)
      return response.data
    },
    onSuccess: () => {
      toast.success('auth.registerSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Registration failed')
    },
  })
}

// Email verification mutation
export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await api.post('/email/verify', { token })
      return response.data
    },
    onSuccess: () => {
      toast.success('auth.emailVerified')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Email verification failed')
    },
  })
}

// Forgot password mutation
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    },
    onSuccess: () => {
      toast.success('auth.passwordResetEmailSent')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to send reset email')
    },
  })
}

// Reset password mutation
export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: ResetPasswordBody) => {
      const response = await api.post('/auth/reset-password', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('auth.passwordResetSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Password reset failed')
    },
  })
}

// Update profile mutation
export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: UpdateProfileBody) => {
      const response = await api.put('/user/profile', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authQueryKeys.user() })
      toast.success('auth.profileUpdated')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to update profile')
    },
  })
}

// Change password mutation
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: UserChangePasswordBody) => {
      const response = await api.post('/user/change-password', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('auth.passwordChanged')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to change password')
    },
  })
}

// Complete onboarding mutation
export const useCompleteOnboarding = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/user/complete-onboarding')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authQueryKeys.user() })
      toast.success('auth.onboardingCompleted')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to complete onboarding')
    },
  })
}

// Context consumer hook - returns auth state from AuthContext
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}